/**
 * API client for the four /photo-capture/* endpoints that drive a
 * session-create → presign → R2 PUT → confirm → complete upload flow.
 *
 * The iPhone capture app is the primary caller, but the operator-facing
 * "Upload Images" button on Vehicle Detail uses the same endpoints so an
 * operator-uploaded batch is just a session attributed to that operator.
 *
 * Response shapes mirror backend/schemas.py.
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ""
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `HTTP ${res.status}`)
  }
  return res
}

// --- Response shapes (mirror backend/schemas.py) ---

export interface CreateSessionRequest {
  rooftop_slug: string
  // At least one of vin / stock must be present. Stock-only is the
  // escape hatch for CTA campers, trailers, accessory bundles that
  // have no VIN — the backend enforces "vin OR stock" at validation.
  vin?: string | null
  stock?: string | null
  client_session_uuid: string
  // Optional operator overrides — used by the Add Vehicle modal so a
  // corrected vPIC decode isn't silently re-decoded server-side. Omit on
  // the normal photographer Upload Images path; the backend will decode
  // the VIN itself (or skip the decode if VIN is null).
  year?: number | null
  make?: string | null
  model?: string | null
}

export interface VinDecodeResult {
  vin: string
  year: number | null
  make: string | null
  model: string | null
}

export interface SessionResponse {
  id: number
  rooftop_id: number
  vin: string | null
  stock: string | null
  year: number | null
  make: string | null
  model: string | null
  photographer_user_id: number
  status: string
  client_session_uuid: string
  started_at: string
  completed_at: string | null
  pushed_at: string | null
  export_attempts: number
  export_last_error: string | null
}

export interface PresignPhotoSpec {
  client_photo_uuid: string
  display_order: number
  content_type?: string
}

export interface PresignedPhoto {
  photo_id: number
  client_photo_uuid: string
  presigned_put_url: string
  r2_key: string
}

export interface PresignResponse {
  photos: PresignedPhoto[]
}

export interface ConfirmPhotoFields {
  width?: number | null
  height?: number | null
  bytes?: number | null
  captured_at?: string | null
}

export interface ConfirmPhotoResponse {
  photo_id: number
  state: string
}

export interface CompleteSessionResponse {
  id: number
  status: string
  n_photos: number
}

// --- Fetchers ---

/**
 * Preview the NHTSA vPIC decode for a VIN without creating a session.
 * Used by the Add Vehicle Not in Feed modal so the operator can confirm
 * or edit year/make/model before committing. Blanks in the response are
 * valid — NHTSA didn't recognize the VIN and the operator can still
 * type the identity in manually.
 */
export async function decodeVin(vin: string): Promise<VinDecodeResult> {
  const url = `${API.baseUrl}/photo-capture/vin-decode/${encodeURIComponent(vin)}`
  const res = await authFetch(url)
  return res.json()
}

export async function createSession(
  payload: CreateSessionRequest,
): Promise<SessionResponse> {
  const res = await authFetch(`${API.baseUrl}${API.endpoints.photoCapture.sessions}`, {
    method: "POST",
    body: JSON.stringify(payload),
  })
  return res.json()
}

export async function presignPhotos(
  sessionId: number,
  photos: PresignPhotoSpec[],
): Promise<PresignResponse> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoCapture.presign(sessionId)}`,
    { method: "POST", body: JSON.stringify({ photos }) },
  )
  return res.json()
}

export async function confirmPhoto(
  sessionId: number,
  photoId: number,
  fields: ConfirmPhotoFields,
): Promise<ConfirmPhotoResponse> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoCapture.confirmPhoto(sessionId, photoId)}`,
    { method: "POST", body: JSON.stringify(fields) },
  )
  return res.json()
}

export async function completeSession(
  sessionId: number,
): Promise<CompleteSessionResponse> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoCapture.complete(sessionId)}`,
    { method: "POST" },
  )
  return res.json()
}

/**
 * Upload bytes directly to R2 via a presigned PUT URL. The Content-Type
 * header MUST match the value passed to presign — R2 signs it into the
 * URL, so a mismatch fails with SignatureDoesNotMatch.
 */
export async function uploadToR2(
  presignedUrl: string,
  file: File,
  contentType: string,
): Promise<void> {
  const res = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(`R2 upload failed: ${body || `HTTP ${res.status}`}`)
  }
}
